/**
 * REW-929 Paper Journal Redux slice.
 *
 * List/uploads/upload-modal state plus durable-row drill-down and lookups.
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/store';
import { paperJournalService } from '@/app/services/paperJournalService';
import type { PaperJournalUploadErrorPayload } from '@/app/utils/paperJournalUploadError';
import { extractPaperJournalUploadError } from '@/app/utils/paperJournalUploadError';
import type {
  PaperJournalListUploadsParams,
  PaperJournalPaginatedRewardRows,
  PaperJournalRewardRow,
  PaperJournalSummaryWindow,
  PaperJournalUploadHistoryItem,
  PaperJournalUploadHistoryResponse,
  PaperJournalUploadResponse,
  PaperJournalUploadSummary,
} from '@/app/types/paperJournal';

export type PaperJournalDetailLoadStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

// ============================================================================
// Constants
// ============================================================================

export const PAPER_JOURNAL_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PaperJournalPageSizeOption = typeof PAPER_JOURNAL_PAGE_SIZE_OPTIONS[number];

export const PAPER_JOURNAL_LOOKUP_PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export type PaperJournalLookupPageSizeOption =
  typeof PAPER_JOURNAL_LOOKUP_PAGE_SIZE_OPTIONS[number];

const DEFAULT_PAGE_SIZE: PaperJournalPageSizeOption = 10;
const DEFAULT_LOOKUP_PAGE_SIZE: PaperJournalLookupPageSizeOption = 25;

// ============================================================================
// Types
// ============================================================================

export type PaperJournalUploadError = PaperJournalUploadErrorPayload;

interface PaperJournalState {
  items: PaperJournalUploadHistoryItem[];
  totalCount: number;
  summary: PaperJournalUploadSummary | null;
  currentPage: number;
  pageSize: PaperJournalPageSizeOption;
  filters: PaperJournalListUploadsParams;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  uploading: boolean;
  lastUploadResponse: PaperJournalUploadResponse | null;
  uploadError: PaperJournalUploadError | null;
  /** GET .../uploads/{sha}/rows */
  uploadRows: PaperJournalRewardRow[];
  uploadRowsStatus: PaperJournalDetailLoadStatus;
  uploadRowsError: string | null;
  uploadRowsFileSha256: string | null;
  /** GET .../users/{id}/rows or .../eligible-participants/{id}/rows */
  lookupRows: PaperJournalRewardRow[];
  lookupTotal: number;
  lookupStatus: PaperJournalDetailLoadStatus;
  lookupError: string | null;
  lookupMode: 'user' | 'epid' | null;
  lookupCurrentPage: number;
  lookupPageSize: PaperJournalLookupPageSizeOption;
}

// ============================================================================
// Initial State
// ============================================================================

const initialFilters: PaperJournalListUploadsParams = {
  limit: DEFAULT_PAGE_SIZE,
  offset: 0,
};

const initialState: PaperJournalState = {
  items: [],
  totalCount: 0,
  summary: null,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  filters: initialFilters,
  status: 'idle',
  error: null,
  uploading: false,
  lastUploadResponse: null,
  uploadError: null,
  uploadRows: [],
  uploadRowsStatus: 'idle',
  uploadRowsError: null,
  uploadRowsFileSha256: null,
  lookupRows: [],
  lookupTotal: 0,
  lookupStatus: 'idle',
  lookupError: null,
  lookupMode: null,
  lookupCurrentPage: 1,
  lookupPageSize: DEFAULT_LOOKUP_PAGE_SIZE,
};

// ============================================================================
// Async Thunks
// ============================================================================

export const fetchPaperJournalUploadsThunk = createAsyncThunk<
  PaperJournalUploadHistoryResponse,
  PaperJournalListUploadsParams | undefined,
  { state: RootState; rejectValue: string }
>('paperJournal/fetchUploads', async (params, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const filters = params ?? state.paperJournal.filters;
    return await paperJournalService.getPaperJournalUploads(filters);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to fetch paper journal uploads',
    );
  }
});

export const uploadPaperJournalThunk = createAsyncThunk<
  PaperJournalUploadResponse,
  File,
  { state: RootState; rejectValue: PaperJournalUploadError }
>('paperJournal/uploadPaperJournal', async (file, { rejectWithValue }) => {
  try {
    return await paperJournalService.uploadPaperJournal(file);
  } catch (error) {
    return rejectWithValue(extractPaperJournalUploadError(error));
  }
});

/** @deprecated Use uploadPaperJournalThunk — alias kept for incremental migration */
export const uploadPaperJournalCsvThunk = uploadPaperJournalThunk;

export const fetchPaperJournalUploadRowsThunk = createAsyncThunk<
  PaperJournalRewardRow[],
  string,
  { state: RootState; rejectValue: string }
>('paperJournal/fetchUploadRows', async (fileSha256, { rejectWithValue }) => {
  try {
    return await paperJournalService.getPaperJournalUploadRows(fileSha256);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to load upload rows',
    );
  }
});

export interface PaperJournalUserRowsQuery {
  refUserId: number;
  limit: number;
  offset: number;
}

export const fetchPaperJournalUserRowsThunk = createAsyncThunk<
  PaperJournalPaginatedRewardRows,
  PaperJournalUserRowsQuery,
  { state: RootState; rejectValue: string }
>('paperJournal/fetchUserRows', async ({ refUserId, limit, offset }, { rejectWithValue }) => {
  try {
    return await paperJournalService.getPaperJournalUserRows(refUserId, limit, offset);
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : 'Failed to load rows for user',
    );
  }
});

export interface PaperJournalEpidRowsQuery {
  eligibleParticipantId: string | number;
  limit: number;
  offset: number;
}

export const fetchPaperJournalEpidRowsThunk = createAsyncThunk<
  PaperJournalPaginatedRewardRows,
  PaperJournalEpidRowsQuery,
  { state: RootState; rejectValue: string }
>(
  'paperJournal/fetchEpidRows',
  async ({ eligibleParticipantId, limit, offset }, { rejectWithValue }) => {
    try {
      return await paperJournalService.getPaperJournalEpidRows(
        eligibleParticipantId,
        limit,
        offset,
      );
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to load rows for EPID',
      );
    }
  },
);

// ============================================================================
// Slice
// ============================================================================

const paperJournalSlice = createSlice({
  name: 'paperJournal',
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<PaperJournalListUploadsParams>>,
    ) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        offset: 0,
      };
      state.currentPage = 1;
    },

    setSummaryWindow: (
      state,
      action: PayloadAction<PaperJournalSummaryWindow | undefined>,
    ) => {
      state.filters = { ...state.filters, summary_window: action.payload };
    },

    setPage: (state, action: PayloadAction<number>) => {
      const page = action.payload;
      state.currentPage = page;
      state.filters = { ...state.filters, offset: (page - 1) * state.pageSize };
    },

    setPageSize: (state, action: PayloadAction<PaperJournalPageSizeOption>) => {
      const newSize = action.payload;
      state.pageSize = newSize;
      state.filters = { ...state.filters, limit: newSize, offset: 0 };
      state.currentPage = 1;
    },

    clearFilters: (state) => {
      state.filters = { limit: state.pageSize, offset: 0 };
      state.currentPage = 1;
    },

    clearLastUpload: (state) => {
      state.lastUploadResponse = null;
      state.uploadError = null;
    },

    setLookupPageSize: (state, action: PayloadAction<PaperJournalLookupPageSizeOption>) => {
      state.lookupPageSize = action.payload;
      state.lookupCurrentPage = 1;
    },

    clearLookupRows: (state) => {
      state.lookupRows = [];
      state.lookupTotal = 0;
      state.lookupStatus = 'idle';
      state.lookupError = null;
      state.lookupMode = null;
      state.lookupCurrentPage = 1;
    },

    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaperJournalUploadsThunk.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPaperJournalUploadsThunk.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.items = action.payload.items;
        state.totalCount = action.payload.total;
        state.summary = action.payload.summary;
      })
      .addCase(fetchPaperJournalUploadsThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'Unknown error';
      })
      .addCase(uploadPaperJournalThunk.pending, (state) => {
        state.uploading = true;
        state.uploadError = null;
      })
      .addCase(uploadPaperJournalThunk.fulfilled, (state, action) => {
        state.uploading = false;
        state.lastUploadResponse = action.payload;
        state.uploadError = null;
      })
      .addCase(uploadPaperJournalThunk.rejected, (state, action) => {
        state.uploading = false;
        state.uploadError = action.payload ?? { status: null, message: 'Upload failed' };
      })
      .addCase(fetchPaperJournalUploadRowsThunk.pending, (state, action) => {
        state.uploadRowsStatus = 'loading';
        state.uploadRowsError = null;
        state.uploadRowsFileSha256 = action.meta.arg;
      })
      .addCase(fetchPaperJournalUploadRowsThunk.fulfilled, (state, action) => {
        state.uploadRowsStatus = 'succeeded';
        state.uploadRows = action.payload;
      })
      .addCase(fetchPaperJournalUploadRowsThunk.rejected, (state, action) => {
        state.uploadRowsStatus = 'failed';
        state.uploadRowsError = action.payload ?? 'Unknown error';
      })
      .addCase(fetchPaperJournalUserRowsThunk.pending, (state, action) => {
        state.lookupStatus = 'loading';
        state.lookupError = null;
        state.lookupMode = 'user';
        state.lookupCurrentPage = Math.floor(action.meta.arg.offset / action.meta.arg.limit) + 1;
      })
      .addCase(fetchPaperJournalUserRowsThunk.fulfilled, (state, action) => {
        state.lookupStatus = 'succeeded';
        state.lookupRows = action.payload.items;
        state.lookupTotal = action.payload.total;
      })
      .addCase(fetchPaperJournalUserRowsThunk.rejected, (state, action) => {
        state.lookupStatus = 'failed';
        state.lookupError = action.payload ?? 'Unknown error';
      })
      .addCase(fetchPaperJournalEpidRowsThunk.pending, (state, action) => {
        state.lookupStatus = 'loading';
        state.lookupError = null;
        state.lookupMode = 'epid';
        state.lookupCurrentPage = Math.floor(action.meta.arg.offset / action.meta.arg.limit) + 1;
      })
      .addCase(fetchPaperJournalEpidRowsThunk.fulfilled, (state, action) => {
        state.lookupStatus = 'succeeded';
        state.lookupRows = action.payload.items;
        state.lookupTotal = action.payload.total;
      })
      .addCase(fetchPaperJournalEpidRowsThunk.rejected, (state, action) => {
        state.lookupStatus = 'failed';
        state.lookupError = action.payload ?? 'Unknown error';
      });
  },
});

export const {
  setFilters,
  setSummaryWindow,
  setPage,
  setPageSize,
  clearFilters,
  clearLastUpload,
  setLookupPageSize,
  clearLookupRows,
  resetState,
} = paperJournalSlice.actions;

export const selectPaperJournalUploads = (state: RootState) =>
  state.paperJournal.items;
export const selectPaperJournalTotalCount = (state: RootState) =>
  state.paperJournal.totalCount;
export const selectPaperJournalSummary = (state: RootState) =>
  state.paperJournal.summary;
export const selectPaperJournalCurrentPage = (state: RootState) =>
  state.paperJournal.currentPage;
export const selectPaperJournalPageSize = (state: RootState) =>
  state.paperJournal.pageSize;
export const selectPaperJournalFilters = (state: RootState) =>
  state.paperJournal.filters;
export const selectPaperJournalStatus = (state: RootState) =>
  state.paperJournal.status;
export const selectPaperJournalError = (state: RootState) =>
  state.paperJournal.error;
export const selectPaperJournalIsLoading = (state: RootState) =>
  state.paperJournal.status === 'loading';
export const selectPaperJournalTotalPages = (state: RootState) =>
  Math.ceil(state.paperJournal.totalCount / state.paperJournal.pageSize);
export const selectPaperJournalUploading = (state: RootState) =>
  state.paperJournal.uploading;
export const selectPaperJournalLastUploadResponse = (state: RootState) =>
  state.paperJournal.lastUploadResponse;
export const selectPaperJournalUploadError = (state: RootState) =>
  state.paperJournal.uploadError;

export const selectPaperJournalUploadRows = (state: RootState) =>
  state.paperJournal.uploadRows;
export const selectPaperJournalUploadRowsStatus = (state: RootState) =>
  state.paperJournal.uploadRowsStatus;
export const selectPaperJournalUploadRowsError = (state: RootState) =>
  state.paperJournal.uploadRowsError;
export const selectPaperJournalUploadRowsFileSha256 = (state: RootState) =>
  state.paperJournal.uploadRowsFileSha256;

export const selectPaperJournalLookupRows = (state: RootState) =>
  state.paperJournal.lookupRows;
export const selectPaperJournalLookupTotal = (state: RootState) =>
  state.paperJournal.lookupTotal;
export const selectPaperJournalLookupStatus = (state: RootState) =>
  state.paperJournal.lookupStatus;
export const selectPaperJournalLookupError = (state: RootState) =>
  state.paperJournal.lookupError;
export const selectPaperJournalLookupMode = (state: RootState) =>
  state.paperJournal.lookupMode;
export const selectPaperJournalLookupCurrentPage = (state: RootState) =>
  state.paperJournal.lookupCurrentPage;
export const selectPaperJournalLookupPageSize = (state: RootState) =>
  state.paperJournal.lookupPageSize;
export const selectPaperJournalLookupTotalPages = (state: RootState) => {
  const { lookupTotal, lookupPageSize } = state.paperJournal;
  return Math.ceil(lookupTotal / lookupPageSize) || 1;
};

export default paperJournalSlice.reducer;
